import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { categoryApi } from "@/api/services/categories";
import type {
  CategorySearchParams,
  CategoryCreate,
  CategoryUpdate,
} from "@/types/services/api-types/category-types";
import { useLoadOptions } from "@/hooks/use-load-options";
import type { Category } from "@/types/services/api-types/category-types";

export const useGetCategoriesPaginated = (
  params: CategorySearchParams = {}
) => {
  return useQuery({
    queryKey: ["categories", params],
    queryFn: () => categoryApi.getCategories(params),
    placeholderData: keepPreviousData,
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CategoryCreate) => categoryApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CategoryUpdate }) =>
      categoryApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      // Invalidate services query since services display category names
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => categoryApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
};

const categoryQueryConfig = {
  queryKey: (params: CategorySearchParams) => ["categories", params] as const,
  queryFn: (params: CategorySearchParams) => categoryApi.getCategories(params),
};

export const useCategoryLoadOptions = () => {
  return useLoadOptions({
    queryConfig: categoryQueryConfig,
    mapToOption: (category: Category) => ({
      value: category.id,
      label: category.name,
    }),
    getParams: (
      search: string,
      page: number,
      size: number
    ): CategorySearchParams => ({
      page,
      size,
      name: search || undefined,
    }),
    pageSize: 15,
  });
};
